import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { Row, Col, Select } from 'antd';
import { partnerTypes } from 'utils/app-configs';
import { sanitizeName } from 'utils/helpers';
import { Div, Flex, FormItem, Button, Icon } from 'ui';
import PartnerInfo from './partner-info';
import ContactPersonInfo from './contact-person-info';

const { Option, OptGroup } = Select;

const Buttons = styled(Div)`
  > button {
    margin-right: 5px;
    text-transform: uppercase;
  }
`;

const StyledPartnerInfo = styled(Flex)`
  margin: 15px 0 20px 0;
  padding: 10px;
  background-color: #ECF2F6;
  .highlight {
    font-weight: 700;
    color: ${p => p.theme.colors.theme};
  }
`;

const StyledIcon = styled(Icon)`
  font-size: 26px;
  color: ${p => p.theme.colors.theme};
`;

const StyledRow = styled(Row)`
  font-size: 16px;
  margin-top: 40px;
  color: ${p => p.theme.colors.themeSecondary};
`;


const StyledSelect = styled(Select)`
  font-size: 16px;
  font-weight: 400;
`;

const RelativeFormItem = styled(FormItem)`
  position: relative;
`;

const AddIcon = styled.div`
  position: absolute;
  left: 105%;
  top: -12px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-size: 25px;
  color: ${p => p.theme.colors.theme};
  i {
    cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  }
`;

const AdvancedChangePartnerForm = ({
  partnerValidator,
  data,
  type,
  renderLine,
  isSaving,
  engineer,
  preferredEngineer,
  lastVisitEngineer,
  intl,
  selectedPartner,
  handleSelect,
  gotoDetails,
  savePartnerDetailsRequest,
  addPartnerDetailsRequest,
  typeName,
  form,
  lastUsed,
  canEditPartner,
  entityId,
  saveDetailsSuccess,
  showEditPartnerRegion,
}) => {
  const [editModeEnabled, setEditModeEnabled] = useState(false);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    if (saveDetailsSuccess) {
      setEditModeEnabled(false);
      setIsNew(false);
    }
  }, [saveDetailsSuccess]);

  useEffect(() => {
    if (editModeEnabled) {
      form.resetFields();
      form.setFieldsValue({
        partner: '',
      });
    } else {
      form.setFieldsValue({
        partner: selectedPartner?.id,
      });
    }
  }, [editModeEnabled]);

  const handleAddNew = () => {
    setEditModeEnabled(true);
    setIsNew(true);
  };

  const renderOptions = () => {
    if (lastUsed?.length) {
      return (
        <>
          <OptGroup label={intl.formatMessage({ id: 'partners.LAST_USED' })}>
            {lastUsed?.map(s =>
              <Option value={s?.cosmosId} key={s?.cosmosId} isLastUsed={true}>
                {/* eslint-disable-next-line */}
                {`${s.partnerDetails?.name} - ${s.partnerDetails?.city} ${s.partnerDetails?.street} ${s.partnerDetails?.houseNumber}`}
              </Option>,
            )}
          </OptGroup>
          <OptGroup label={intl.formatMessage({ id: 'partners.ALL' })}>
            {data?.map(s =>
              <Option value={s.id} key={`all_${s.id}`}>
                {s.isDefault ?
                  `(${intl.formatMessage({ id: 'partners.DEFAULT' })}) ${s.line1}` :
                  s.line1}
              </Option>,
            )}
          </OptGroup>
        </>
      );
    }
    return data?.map(s =>
      <Option value={s.id} key={s.id}>
        {s.isDefault ? `(${intl.formatMessage({ id: 'partners.DEFAULT' })}) ${s.line1}` : s.line1}
      </Option>,
    );
  };

  return (
    <>
      <Div width={'70%'} marginBottom={15} marginTop={50}>
        <RelativeFormItem label={typeName.replace('-', ' ')}>
          {partnerValidator(
            <StyledSelect
              getPopupContainer={triggerNode => triggerNode.parentNode}
              onChange={handleSelect}
              disabled={editModeEnabled || !data || !Object.keys(data).length}
              valueKey='id'
              valueLabel='displayLine'
              showSearch={true}
              placeholder={
                intl.formatMessage({
                  // eslint-disable-next-line max-len
                  id: `partners.PLACEHOLDER_${typeName ? sanitizeName(typeName).toUpperCase() : 'DEFAULT'}`,
                })
              }
              filterOption={(input, option) =>
                option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
              }
            >
              {renderOptions()}
            </StyledSelect>,
          )}
          {type === partnerTypes.CONTACTPERSON && isToggleEnabled('canEditPartner') ?
            <AddIcon
              onClick={!editModeEnabled && handleAddNew}
              disabled={isSaving || editModeEnabled}
            >
              <Icon type='plus' solid={true} />
            </AddIcon> :
            null
          }
        </RelativeFormItem>
        {(selectedPartner || isNew) &&
          <StyledPartnerInfo fdc>
            {type === partnerTypes.CONTACTPERSON ?
              <ContactPersonInfo
                editModeEnabled={editModeEnabled}
                form={form}
                selectedPartner={selectedPartner}
                typeName={typeName.replace('-', ' ')}
                isSaving={isSaving}
                savePartnerDetailsRequest={savePartnerDetailsRequest}
                addPartnerDetailsRequest={addPartnerDetailsRequest}
                type={type}
                entityId={entityId}
                renderLine={renderLine}
                canEditPartner={canEditPartner}
                setEditModeEnabled={setEditModeEnabled}
                isNew={isNew}
                setIsNew={setIsNew}
              /> :
              <PartnerInfo
                editModeEnabled={editModeEnabled}
                form={form}
                selectedPartner={selectedPartner}
                typeName={typeName}
                isSaving={isSaving}
                savePartnerDetailsRequest={savePartnerDetailsRequest}
                type={type}
                entityId={entityId}
                renderLine={renderLine}
                canEditPartner={canEditPartner}
                setEditModeEnabled={setEditModeEnabled}
                showEditPartnerRegion={showEditPartnerRegion}
              />

            }
          </StyledPartnerInfo>
        }
      </Div>
      {!editModeEnabled &&
        <Buttons>
          <Button onClick={gotoDetails} disabled={editModeEnabled}>
            <FormattedMessage id='partners.CANCEL_BTN' />
          </Button>
          <Button
            type='secondary'
            htmlType='submit'
            loading={isSaving}
            disabled={editModeEnabled || !data || !Object.keys(data).length}
          >
            {/* eslint-disable-next-line max-len */}
            {`${intl.formatMessage({ id: 'partners.SELECT' })} ${typeName?.replace('-', ' ')?.toUpperCase()}`}
          </Button>
        </Buttons>
      }
      {
        type === partnerTypes.SHIPTO && <StyledRow>
          <Col span={1}>
            <StyledIcon type='info-circle' />
          </Col>
          <Col span={23}>
            <p>
              {engineer && intl.formatMessage(
                { id: 'partners.CURRENT_ENGINEER' },
                { engineer },
              )}
            </p>
            <p>
              {preferredEngineer && intl.formatMessage(
                { id: 'partners.PREFERRED_ENGINEER' },
                { engineer: preferredEngineer },
              )}
            </p>
            <p>
              {lastVisitEngineer && intl.formatMessage(
                { id: 'partners.LAST_VISIT_ENGINEER' },
                { engineer: lastVisitEngineer },
              )}
            </p>
          </Col>
        </StyledRow>
      }
    </>
  );
};
AdvancedChangePartnerForm.propTypes = {
  partnerValidator: PropTypes.func,
  data: PropTypes.object,
  isSaving: PropTypes.bool,
  engineer: PropTypes.string,
  preferredEngineer: PropTypes.string,
  lastVisitEngineer: PropTypes.string,
  intl: PropTypes.object,
  selectedPartner: PropTypes.string,
  renderLine: PropTypes.func,
  type: PropTypes.string,
  handleSelect: PropTypes.func,
  gotoDetails: PropTypes.func,
  typeName: PropTypes.string,
  savePartnerDetailsRequest: PropTypes.func,
  addPartnerDetailsRequest: PropTypes.func,
  form: PropTypes.object,
  lastUsed: PropTypes.array,
  canEditPartner: PropTypes.bool,
  entityId: PropTypes.string,
  saveDetailsSuccess: PropTypes.bool,
  showEditPartnerRegion: PropTypes.bool,
};

export default injectIntl(AdvancedChangePartnerForm);
